import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ScrollToTop from './components/common/ScrollToTop';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

// Public pages
import HomePage          from './pages/HomePage';
import ProductsPage      from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import InquiryPage       from './pages/InquiryPage';
import PriceListPage     from './pages/PriceListPage';
import MeasureGuidesPage, { MeasureGuideDetailPage } from './pages/MeasureGuidesPage';
import AboutPage         from './pages/AboutPage';
import FeedbackPage      from './pages/FeedbackPage';
import LoginPage         from './pages/LoginPage';
import RegisterPage      from './pages/RegisterPage';
import ProfilePage       from './pages/ProfilePage';

// Admin pages
import AdminLayout        from './pages/admin/AdminLayout';
import AdminDashboard     from './pages/admin/AdminDashboard';
import AdminProducts      from './pages/admin/AdminProducts';
import AdminCategories    from './pages/admin/AdminCategories';
import AdminDealers       from './pages/admin/AdminDealers';
import AdminInquiries     from './pages/admin/AdminInquiries';
import AdminPriceLists    from './pages/admin/AdminPriceLists';
import AdminMeasureGuides from './pages/admin/AdminMeasureGuides';
import AdminFeedback      from './pages/admin/AdminFeedback';
import AdminContent       from './pages/admin/AdminContent';
import AdminUsers         from './pages/admin/AdminUsers';
import AdminCertificates  from './pages/admin/AdminCertificates';
import AdminStockGroups   from './pages/admin/AdminStockGroups';
import CurrentStockPage   from './pages/CurrentStockPage';

/* ── Route guards ─────────────────────────────────────────────────────────── */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)                 return <Navigate to="/admin/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => (
  <Routes>
    {/* Public */}
    <Route path="/"                   element={<HomePage />} />
    <Route path="/products"           element={<ProductsPage />} />
    <Route path="/products/:id"       element={<ProductDetailPage />} />
    <Route path="/price-list"         element={<PriceListPage />} />
    <Route path="/current-stock"      element={<CurrentStockPage />} />
    <Route path="/how-to-measure"     element={<MeasureGuidesPage />} />
    <Route path="/how-to-measure/:id" element={<MeasureGuideDetailPage />} />
    <Route path="/about"              element={<AboutPage />} />
    <Route path="/feedback"           element={<FeedbackPage />} />
    <Route path="/login"              element={<LoginPage />} />
    <Route path="/admin/login"        element={<LoginPage adminMode />} />
    <Route path="/register"           element={<RegisterPage />} />
    <Route path="/inquiry"            element={<InquiryPage />} />

    {/* Protected user */}
    <Route path="/profile"      element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

    {/* Admin */}
    <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
      <Route index                   element={<AdminDashboard />} />
      <Route path="products"         element={<AdminProducts />} />
      <Route path="categories"       element={<AdminCategories />} />
      <Route path="dealers"          element={<AdminDealers />} />
      <Route path="inquiries"        element={<AdminInquiries />} />
      <Route path="price-lists"      element={<AdminPriceLists />} />
      <Route path="measure-guides"   element={<AdminMeasureGuides />} />
      <Route path="feedback"         element={<AdminFeedback />} />
      <Route path="content"          element={<AdminContent />} />
      <Route path="users"            element={<AdminUsers />} />
      <Route path="certificates"     element={<AdminCertificates />} />
      <Route path="stock-groups"     element={<AdminStockGroups />} />
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#1b1c1c', color: '#fcf9f8',
                borderRadius: '0px', fontFamily: 'Inter, sans-serif',
                fontSize: '13px', fontWeight: '600',
              },
              success: { iconTheme: { primary: '#ff6600', secondary: '#fff' } },
              error:   { iconTheme: { primary: '#ba1a1a', secondary: '#fff' } },
            }}
          />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
